import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { T } from '@tolgee/react'
import {
  forwardRef,
  HTMLAttributes,
  TdHTMLAttributes,
  ThHTMLAttributes,
  useEffect,
  useRef,
} from 'react'

interface TableProps extends HTMLAttributes<HTMLTableElement> {
  parentClassName?: string
}

const Table = forwardRef<HTMLTableElement, TableProps>(
  ({ className, parentClassName, children, ...props }, ref) => {
    const parentRef = useRef<HTMLDivElement>(null)
    useEffect(() => {
      const handleScroll = () => {
        const parent = parentRef.current
        if (parent) {
          const thead = parent.querySelector('thead')
          if (thead) {
            thead.classList.toggle('shadow-xl', parent.scrollTop > 0)
          }
        }
      }
      if (parentRef.current) {
        parentRef.current.addEventListener('scroll', handleScroll)
      }

      return () => {
        if (parentRef.current) {
          parentRef.current.removeEventListener('scroll', handleScroll)
        }
      }
    }, [])

    return (
      <div
        ref={parentRef}
        className={cn(
          'relative w-full overflow-auto overflow-x-scroll rounded-2xl border border-neutral-75 bg-neutral-white scrollbar-thin scrollbar-track-transparent scrollbar-thumb-black/5',
          parentClassName,
        )}
      >
        <table ref={ref} className={cn('w-full', className)} {...props}>
          {children}
        </table>
      </div>
    )
  },
)

Table.displayName = 'Table'

const TableHeader = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <thead
      ref={ref}
      className={cn('sticky top-0 h-[72px] whitespace-nowrap border-b bg-neutral-50', className)}
      {...props}
    />
  ),
)
TableHeader.displayName = 'TableHeader'

const TableBody = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => <tbody ref={ref} className={cn('', className)} {...props} />,
)
TableBody.displayName = 'TableBody'

const TableFooter = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => <tfoot ref={ref} className={cn('', className)} {...props} />,
)
TableFooter.displayName = 'TableFooter'

const TableRow = forwardRef<HTMLTableRowElement, HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        'has-[button[data-state=open]]:bg-neutral-25 data-[state=selected]:bg-neutral-25 [&:last-child>td]:border-b-0',
        className,
      )}
      {...props}
    />
  ),
)
TableRow.displayName = 'TableRow'

const TableHead = forwardRef<HTMLTableCellElement, ThHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        'border-b border-neutral-75 bg-neutral-50 px-6 py-3 text-left text-sm font-bold data-[state=selected]:bg-neutral-75',
        className,
      )}
      {...props}
    />
  ),
)
TableHead.displayName = 'TableHead'

const TableCell = forwardRef<HTMLTableCellElement, TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <td
      ref={ref}
      className={cn(
        'min-h-16 border-b border-neutral-75 px-6 py-3 text-sm font-light text-neutral-500 data-[state=selected]:bg-neutral-25',
        className,
      )}
      {...props}
    />
  ),
)
TableCell.displayName = 'TableCell'

const TableCaption = forwardRef<HTMLTableCaptionElement, HTMLAttributes<HTMLTableCaptionElement>>(
  ({ className, ...props }, ref) => (
    <caption ref={ref} className={cn('mt-4 text-sm text-neutral-500', className)} {...props} />
  ),
)
TableCaption.displayName = 'TableCaption'

const TableErrorRow = ({ colSpan }: Readonly<{ colSpan: number }>) => (
  <TableRow>
    <TableCell colSpan={colSpan} className='h-24 text-center text-error-300'>
      <T keyName='shared.error.system' />
    </TableCell>
  </TableRow>
)

const TableSkeletonRows = ({
  rowCount = 2,
  cellCount,
  className,
}: {
  rowCount?: number
  cellCount: number
  className?: string
}) =>
  [...Array(rowCount)].map((_, rowIndex) => (
    <TableRow key={`loading-tr-${rowIndex}`} className={cn('h-[72px]', className)}>
      {[...Array(cellCount)].map((_, cellIndex) => (
        <TableCell
          key={`loading-tr-${rowIndex}-td-${cellIndex}`}
          className='border border-x-0 border-t-0 border-neutral-75 px-1.5 py-0 text-neutral-500 first:border-l-0 first:pl-3 last:border-r-0 last:pr-3'
        >
          <Skeleton className='h-5' />
        </TableCell>
      ))}
    </TableRow>
  ))

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableErrorRow,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeletonRows,
}
